import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Download,
  History,
  Info,
  RefreshCw,
  Search,
} from "lucide-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useId, useState } from "react";
import { toast } from "../utils/toast";
import { checkCompound, downloadUpload, getStepHistory } from "../api";
import { wmErrorMessage } from "../api/wm-client";
import {
  ComputedFacilityRow,
  FacilitiesComputation,
  ResolvedWidget,
  StepHistoryEvent,
  StepHistoryFilter,
} from "../dtos";
import {NoteAttachmentList} from "./note-attachments";
import { primaryButtonSx, secondaryButtonSx } from "./field-shell";

/**
 * §Widgets (context): the three read-model widget placements the CRM tab used to DROP silently
 * (finance-widgets' dispatcher hid every unknown kind), which is why several newly-agent-visible
 * steps rendered with a field missing and no explanation:
 *  - compoundCheck     → CompoundCheckCard  (selection summary + routing verdict + the Check button
 *                        the step's submit gate depends on, + the stale-selection warning)
 *  - iscoreFacilities  → FacilitiesTable    (per-facility bureau obligations behind the DBR numbers)
 *  - stepHistory       → StepHistoryPanel   (the step's audit timeline, lazily fetched on expand)
 * All three are presentation over data workflow-manager already serves; only the Check button
 * writes, and it goes through the engine's own endpoint.
 */

const money = (n: number | null | undefined): string =>
  typeof n === "number" && Number.isFinite(n)
    ? n.toLocaleString("en-EG", { maximumFractionDigits: 0 })
    : "—";

const when = (iso: string | undefined): string => {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

/** Shared card chrome so the three widgets sit consistently among the form fields. */
const WidgetCard = ({
  label,
  icon,
  tone = "neutral",
  action,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  tone?: "neutral" | "positive" | "warning";
  action?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors.semantic;
  const border =
    tone === "positive"
      ? colors.border.extension.positive
      : tone === "warning"
        ? colors.border.extension.warning
        : colors.border.secondary;
  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${border}`,
        borderRadius: theme.nawy.radius.lg,
        p: 1.5,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        // §Mobile: label + action button can outgrow a squeezed card — wrap, don't collide
        sx={{ mb: 1, flexWrap: "wrap", rowGap: 0.5, columnGap: 1 }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
          {icon}
          <Typography
            sx={{
              fontSize: 12,
              lineHeight: "18px",
              fontWeight: 600,
              color: colors.text.primary,
            }}
          >
            {label}
          </Typography>
        </Stack>
        {action}
      </Stack>
      {children}
    </Paper>
  );
};

const SummaryRow = ({ label, value }: { label: string; value: string }) => {
  const theme = useTheme();
  const colors = theme.nawy.colors.semantic;
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography sx={{ fontSize: 13, color: colors.text.secondary }}>
        {label}
      </Typography>
      <Typography
        sx={{ fontSize: 13, fontWeight: 500, color: colors.text.primary }}
      >
        {value || "—"}
      </Typography>
    </Stack>
  );
};

/* ───────────────────────────── compoundCheck ───────────────────────────── */

interface CompoundCheckParameters {
  developer?: string;
  compound?: string;
  phase?: string;
  propertyType?: string;
  /** 'preapproved' | 'legal-review' | '' (never checked) */
  routing?: string;
  /** the selection the LAST check actually ran against — the submit gate's stale test */
  checkedDeveloper?: string;
  checkedCompound?: string;
}

/**
 * The compound pre-approval verdict, and the button that produces it. The step's "Save" gates on
 * `compoundCheck.checked.{developer,compound}` matching the live selection, so without this the
 * agent sees a permanently-disabled Submit and no way to clear it.
 */
export const CompoundCheckCard = ({
  widget,
  caseUid,
  disabled,
  onChecked,
  liveFacts,
  saveDraft,
}: {
  widget: ResolvedWidget;
  caseUid: string;
  disabled?: boolean;
  onChecked?: () => void;
  /** the step form's CURRENT values — the card must reflect what the agent just picked, not only
   * what the server last saw (`widget.parameters` is resolved from saved case facts). */
  liveFacts?: Record<string, unknown>;
  /** persist the draft before checking: the engine reads SAVED facts, so an unsaved selection would
   * silently be checked against the previous one. */
  saveDraft?: () => Promise<unknown>;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors.semantic;
  const [busy, setBusy] = useState(false);
  const p = (widget.parameters ?? {}) as CompoundCheckParameters;

  // The engine resolves these by LABEL (CompoundCheckHelper.readField), so match on the key's last
  // segment — the section name is free text and must not be hardcoded here.
  const live = (label: string): string => {
    for (const [key, value] of Object.entries(liveFacts ?? {})) {
      if (key.slice(key.lastIndexOf(".") + 1) === label && typeof value === "string") {
        return value;
      }
    }
    return "";
  };
  const developer = live("Developer") || p.developer || "";
  const compound = live("Compound") || p.compound || "";
  const phase = live("Phase") || p.phase || "";
  const propertyType = live("Property Type") || p.propertyType || "";

  const routing = p.routing ?? "";
  const selected = { developer, compound };
  const hasSelection = Boolean(selected.developer && selected.compound);
  // §Stale-gate: the same comparison the submit action's `requires` performs server-side.
  const stale =
    hasSelection &&
    Boolean(routing) &&
    (p.checkedDeveloper !== selected.developer ||
      p.checkedCompound !== selected.compound);

  const verdict = !routing
    ? {
        tone: "neutral" as const,
        icon: <Info size={16} color={colors.text.tertiary} />,
        text: "Not checked yet — run Check compound to resolve the approval status.",
      }
    : routing === "preapproved"
      ? {
          tone: "positive" as const,
          // design: a 16px FILLED status glyph (fill = the tone, stroke = the white ground)
          icon: (
            <CheckCircle2
              size={16}
              fill={colors.text.extension.positive}
              color={colors.layer.primary}
            />
          ),
          text: "Pre-approved compound — Legal categorisation is skipped.",
        }
      : {
          tone: "warning" as const,
          icon: (
            <AlertTriangle
              size={16}
              fill={colors.text.extension.warning}
              color={colors.layer.primary}
            />
          ),
          // §Check-vs-submit: the check only WRITES the routing verdict — nothing reaches Legal
          // until the step is submitted and the engine opens the Legal review. Past-tense copy here
          // ("routed") read as if the check itself had escalated (live bite, 2026-08-05).
          text: "Not pre-approved — will route to Legal for categorisation when this step is submitted. The required collateral documents appear once Legal assigns a category.",
        };

  const run = async () => {
    setBusy(true);
    try {
      // the check reads SAVED facts — persist what is on screen first, or the agent would be told
      // about the compound they had selected a moment ago.
      await saveDraft?.();
      const result = await checkCompound(caseUid);
      toast.success(
        result.preApproved
          ? "Compound is pre-approved"
          : // §Check-vs-submit: the check resolves the ROUTE, it does not take it — Legal only
            // gets the case when the step is submitted.
            "Compound not pre-approved — it will go to Legal for categorisation on submit"
      );
      onChecked?.();
    } catch (error) {
      toast.error(wmErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <WidgetCard
      label="Compound check"
      tone={stale ? "warning" : verdict.tone}
      icon={<Search size={14} color={colors.text.secondary} />}
      action={
        <Button
          size="small"
          variant={routing && !stale ? "outlined" : "contained"}
          disabled={disabled || busy || !hasSelection}
          onClick={run}
          startIcon={
            busy ? <CircularProgress size={12} /> : <RefreshCw size={13} />
          }
          sx={
            routing && !stale
              ? secondaryButtonSx(theme)
              : primaryButtonSx(theme)
          }
        >
          {routing ? "Re-check" : "Check compound"}
        </Button>
      }
    >
      <Stack spacing={0.75}>
        <SummaryRow label="Developer" value={developer} />
        <SummaryRow label="Compound" value={compound} />
        <SummaryRow label="Phase" value={phase} />
        <SummaryRow label="Property type" value={propertyType} />
      </Stack>

      {/* §MobileQA: the design draws the verdict as a `Small Alert Message` — a tone-tinted band
          (Indicator/Green Light on a green zone, the warning tint on a red zone), radius lg, px sm
          12 / py xs 8, gap 8, a 16px filled glyph and Body/sm 12/18 in Text/Primary. It was a bare
          grey caption. The tint has no token in the warning case (#FFE3CC), so it is taken verbatim
          from the frame, exactly as the start-flow banner takes its orange ramp. */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-start"
        sx={{
          mt: 1.25,
          px: 1.5,
          py: 1,
          borderRadius: theme.nawy.radius.lg,
          backgroundColor:
            stale || verdict.tone === "warning"
              ? "#FFE3CC"
              : verdict.tone === "positive"
                ? colors.indicator.greenLight
                : colors.layer.secondary,
        }}
      >
        <Box sx={{ display: "flex", flexShrink: 0 }}>
          {stale ? (
            <AlertTriangle
              size={16}
              fill={colors.text.extension.warning}
              color={colors.layer.primary}
            />
          ) : (
            verdict.icon
          )}
        </Box>
        <Typography
          sx={{
            fontSize: 12,
            lineHeight: "18px",
            color: colors.text.primary,
          }}
        >
          {stale
            ? "The selection changed since the last check — re-check before submitting."
            : hasSelection
              ? verdict.text
              : "Pick a developer and compound first."}
        </Typography>
      </Stack>
    </WidgetCard>
  );
};

/* ──────────────────────────── iscoreFacilities ─────────────────────────── */

const BUCKET_LABEL: Record<ComputedFacilityRow["bucket"], string> = {
  consumer: "Consumer",
  product: "Product",
  excluded: "Excluded",
};

/**
 * The per-facility bureau obligations behind the DBR numbers. Read-only: adjustments/overrides are a
 * workflow-manager-side capability, and the totals here are the ones the DBR calculator consumes.
 */
export const FacilitiesTable = ({
  facilities,
}: {
  facilities: FacilitiesComputation | null | undefined;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors.semantic;
  const [open, setOpen] = useState(false);

  if (!facilities || !facilities.rows?.length) {
    return (
      <WidgetCard
        label="i-Score facilities (DBR)"
        icon={<Info size={14} color={colors.text.secondary} />}
      >
        <Typography sx={{ fontSize: 12, color: colors.text.tertiary }}>
          No bureau facilities yet — they appear once an i-Score report has been
          pulled for this case.
        </Typography>
      </WidgetCard>
    );
  }

  const rows = facilities.rows;
  const chip = (label: string, tone?: "warning") => (
    <Chip
      key={label}
      size="small"
      label={label}
      sx={{
        height: 22,
        fontSize: 11,
        backgroundColor:
          tone === "warning"
            ? colors.layer.extension.warningLight
            : colors.layer.secondary,
        color:
          tone === "warning"
            ? colors.text.extension.warning
            : colors.text.secondary,
      }}
    />
  );

  return (
    <WidgetCard
      label="i-Score facilities (DBR)"
      icon={<Info size={14} color={colors.text.secondary} />}
      action={
        <Button
          size="small"
          variant="text"
          onClick={() => setOpen((v) => !v)}
          startIcon={
            open ? <ChevronDown size={14} /> : <ChevronRight size={14} />
          }
          sx={{ textTransform: "none", fontSize: 12, whiteSpace: "nowrap" }}
        >
          {open ? "Hide" : `Show ${rows.length}`}
        </Button>
      }
    >
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {chip(`Consumer ${money(facilities.consumerMonthlyObligations)} EGP/mo`)}
        {chip(`Product ${money(facilities.productMonthlyObligations)} EGP/mo`)}
        {facilities.duplicateCount > 0 &&
          chip(`${facilities.duplicateCount} duplicate`, "warning")}
        {facilities.incompleteCount > 0 &&
          chip(`${facilities.incompleteCount} incomplete`, "warning")}
      </Stack>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ overflowX: "auto", mt: 1 }}>
          <Table size="small" sx={{ minWidth: 620 }}>
            <TableHead>
              <TableRow>
                {["Institution", "Type", "Outstanding", "Monthly", "Bucket"].map(
                  (head) => (
                    <TableCell
                      key={head}
                      sx={{ fontSize: 11, color: colors.text.tertiary }}
                    >
                      {head}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.rowId}
                  sx={{ opacity: row.excluded ? 0.55 : 1 }}
                >
                  <TableCell sx={{ fontSize: 12 }}>
                    {row.institutionName ?? "—"}
                    {row.flags?.length ? (
                      <Typography
                        sx={{
                          fontSize: 10,
                          color: colors.text.extension.warning,
                        }}
                      >
                        {row.flags.join(", ")}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{row.loanTypeDesc}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {money(row.outstanding)}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 500 }}>
                    {money(row.monthlyObligation)}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {BUCKET_LABEL[row.bucket]}
                    {row.excludedReason ? (
                      <Typography
                        sx={{ fontSize: 10, color: colors.text.tertiary }}
                      >
                        {row.excludedReason}
                      </Typography>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Collapse>
    </WidgetCard>
  );
};

/* ───────────────────────────── stepHistory ─────────────────────────────── */

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const valueText = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
};

/**
 * A `submission` event's before/after are the WHOLE data objects — dumping them raw floods the
 * timeline, and the per-field `field-change` events beside them already itemize the same edit.
 * Summarise those to a count and let the field rows carry the detail.
 */
const objectDiffSummary = (
  before: unknown,
  after: unknown
): string | null => {
  if (!isObject(before) && !isObject(after)) return null;
  const keys = new Set([
    ...Object.keys(isObject(before) ? before : {}),
    ...Object.keys(isObject(after) ? after : {}),
  ]);
  const changed = [...keys].filter(
    key =>
      JSON.stringify((before as Record<string, unknown>)?.[key]) !==
      JSON.stringify((after as Record<string, unknown>)?.[key])
  );
  return `${changed.length} field${changed.length === 1 ? "" : "s"} changed`;
};

const HistoryFileDownload = ({file}: {file: NonNullable<StepHistoryEvent["file"]>}) => {
  const [downloading, setDownloading] = useState(false);
  const download = async () => {
    if (!file.uid) return;
    setDownloading(true);
    try {
      await downloadUpload(file.uid, file.name ?? "document");
    } catch (error) {
      toast.error(wmErrorMessage(error));
    } finally {
      setDownloading(false);
    }
  };
  if (file.available === false) return <Typography variant="caption">File unavailable</Typography>;
  if (!file.uid) return null;
  return (
    <Button size="small" startIcon={<Download size={13} />} onClick={download}
      disabled={downloading} aria-label={`Download ${file.name ?? "document"}`}
      sx={{textTransform: "none", fontSize: 12}}>
      {downloading ? "Downloading…" : "Download"}
    </Button>
  );
};

const HistoryDecision = ({decision}: {decision: string | undefined}) => {
  if (!decision) return null;
  const statuses = {
    REJECTED: {label: "Rejected", color: "error"},
    CONDITIONAL: {label: "Conditional", color: "warning"},
    ACCEPTED: {label: "Accepted", color: "success"},
    APPROVED: {label: "Approved", color: "success"},
  } as const;
  const status = statuses[decision as keyof typeof statuses];
  return <Chip size="small" variant="outlined" label={status?.label ?? decision}
    color={status?.color ?? "default"} sx={{height: 24, fontSize: 12, fontWeight: 600}} />;
};

const HistoryComment = ({comment}: {comment: string | undefined}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors.semantic;
  if (!comment) return null;
  return (
    <Box component="blockquote" aria-label="Comment" sx={{
      m: 0, mt: 1, p: 1.25, maxWidth: "75ch", borderRadius: theme.nawy.radius.md,
      backgroundColor: colors.layer.secondary,
    }}>
      <Typography sx={{fontSize: 12, fontWeight: 600, color: colors.text.secondary, mb: 0.5}}>
        Comment
      </Typography>
      <Typography dir="auto" sx={{fontSize: 14, lineHeight: "21px", color: colors.text.primary,
        whiteSpace: "pre-wrap", overflowWrap: "anywhere"}}>
        {comment}
      </Typography>
    </Box>
  );
};

const historyAuthor = (actor: StepHistoryEvent["actor"]): string => {
  const types: Record<string, string> = {INTERNAL: "Internal user", EXTERNAL: "External user", SYSTEM: "System", CLIENT: "Customer"};
  return actor?.name?.trim() || types[actor?.type] || "Unknown author";
};

const HistoryRow = ({ event }: { event: StepHistoryEvent }) => {
  const theme = useTheme();
  const colors = theme.nawy.colors.semantic;
  const label = event.field?.label ?? event.field?.key;
  // effect/system events serialize before+after as NULL — only render the arrow for a REAL change
  // (both null/undefined ⇒ nothing changed; `null → "x"` is a genuine first write and does render).
  const hasBefore = event.before !== undefined && event.before !== null;
  const hasAfter = event.after !== undefined && event.after !== null;
  const changed = hasBefore || hasAfter;
  const summary = changed ? objectDiffSummary(event.before, event.after) : null;
  const comment = event.file?.reason ?? (event.kind === "comment-added" ? event.description : undefined);
  const description = event.description === comment ? undefined : event.description;
  return (
    <Box
      component="article"
      aria-label={event.title}
      sx={{
        borderLeft: `2px solid ${colors.border.secondary}`,
        pl: 1.5,
        py: 1,
        minWidth: 0,
      }}
    >
      <Stack direction="row" alignItems="center" sx={{flexWrap: "wrap", gap: 0.75}}>
        <Typography
          sx={{ fontSize: 13, fontWeight: 600, color: colors.text.primary }}
        >
          {event.title}
        </Typography>
        {label ? (
          <Typography sx={{ fontSize: 12, color: colors.text.secondary, overflowWrap: "anywhere" }}>
            {label}
          </Typography>
        ) : null}
        <HistoryDecision decision={event.file?.decision} />
      </Stack>
      <Stack direction="row" sx={{flexWrap: "wrap", columnGap: 1.5, rowGap: 0.25, mt: 0.5, mb: 0.75}}>
        <Typography sx={{fontSize: 12, color: colors.text.secondary, overflowWrap: "anywhere"}}>
          By <Box component="span" sx={{fontWeight: 600, color: colors.text.primary}}>{historyAuthor(event.actor)}</Box>
        </Typography>
        <Typography component="time" dateTime={event.occurredAt} sx={{fontSize: 12, color: colors.text.secondary}}>
          {when(event.occurredAt)}
        </Typography>
      </Stack>
      {changed ? (
        <Typography sx={{ fontSize: 11, color: colors.text.secondary }}>
          {summary ?? `${valueText(event.before)} → ${valueText(event.after)}`}
        </Typography>
      ) : null}
      {description ? (
        <Typography sx={{ fontSize: 12, color: colors.text.secondary, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
          {description}
        </Typography>
      ) : null}
      {event.file?.name ? (
        <Typography sx={{ fontSize: 12, color: colors.text.secondary, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
          File: {event.file.name}
        </Typography>
      ) : null}
      <HistoryComment comment={comment} />
      {event.file && <HistoryFileDownload file={event.file} />}
      <NoteAttachmentList attachments={event.attachments} />
    </Box>
  );
};

/**
 * The step's audit timeline. Fetched lazily on first expand — a phase can carry several history
 * placements and the board would otherwise fire one request per card on every render.
 */
export const StepHistoryPanel = ({
  stepUid,
  groupBySourceStep,
  query,
  inline = false,
}: {
  stepUid: string | undefined;
  /** widget parameter `defaultView: 'grouped'` — group events under the step that produced them */
  groupBySourceStep?: boolean;
  query?: StepHistoryFilter;
  /** A compact disclosure directly beneath a document, independent of whole-step history. */
  inline?: boolean;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors.semantic;
  const [open, setOpen] = useState(false);
  const contentId = useId();
  const scopeKey = JSON.stringify([stepUid, query?.scope, query?.fieldKey, query?.sourceStepOrder]);
  const [olderError, setOlderError] = useState<{scopeKey: string; error: unknown; at: number}>();
  // §Realtime: under the ["nawy-now"] prefix so SSE invalidation + the poll floor refresh an OPEN history
  // panel; stays lazy (enabled on first expand) so a collapsed panel costs nothing, exactly as before.
  const {
    data,
    isLoading: loading,
    isError: failed,
    error,
    errorUpdatedAt,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["nawy-now", "step-history", stepUid ?? null, "pages", query ?? {}],
    enabled: open && Boolean(stepUid),
    queryFn: ({pageParam}) => getStepHistory(stepUid as string, {...query, limit: 30, ...(pageParam ? {cursor: pageParam} : {})}),
    getNextPageParam: page => page.nextCursor ?? undefined,
  });
  const events = Array.from(new Map((data?.pages.flatMap(page => page.items ?? []) ?? []).map(event => [event.id, event])).values());
  const isFetchNextPageError = failed && olderError?.scopeKey === scopeKey && olderError?.error === error && olderError?.at === errorUpdatedAt;
  const loadOlder = async () => {
    const result = await fetchNextPage({throwOnError: false});
    setOlderError(result.isError ? {scopeKey, error: result.error, at: result.errorUpdatedAt} : undefined);
  };

  const load = () => refetch();

  const toggle = () => {
    setOpen(!open);
  };

  const grouped = (): [string, StepHistoryEvent[]][] => {
    if (!events) return [];
    if (!groupBySourceStep) return [["", events]];
    const map = new Map<string, StepHistoryEvent[]>();
    for (const event of events) {
      const key = event.originStep?.name ?? "This step";
      map.set(key, [...(map.get(key) ?? []), event]);
    }
    return [...map.entries()];
  };

  const olderHistoryLabel = () => {
    if (isFetchingNextPage) return "Loading older history…";
    return isFetchNextPageError ? "Retry older history" : "Load older history";
  };

  const renderHistory = () => {
    if (loading)
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={14} />
          <Typography sx={{ fontSize: 12, color: colors.text.tertiary }}>Loading history…</Typography>
        </Stack>
      );
    if (failed && !events.length)
      return (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography sx={{ fontSize: 12, color: colors.text.extension.negative }}>
            {inline ? "Couldn't load this document's history." : "Couldn't load this step's history."}
          </Typography>
          <Button
            size="small"
            variant="text"
            onClick={load}
            sx={{ textTransform: "none", fontSize: 12 }}
          >
            Retry
          </Button>
        </Stack>
      );
    if (!events.length)
      return (
        <Typography sx={{ fontSize: 12, color: colors.text.tertiary }}>
          {inline ? "No history for this document yet." : "Nothing has happened on this step yet."}
        </Typography>
      );
    return (
      <Stack spacing={1.25}>
        {grouped().map(([group, list]) => (
          <Box key={group || "all"}>
            {group ? (
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: colors.text.secondary,
                  mb: 0.5,
                }}
              >
                {group}
              </Typography>
            ) : null}
            <Stack spacing={0.75}>
              {list.map((event) => (
                <HistoryRow key={event.id} event={event} />
              ))}
            </Stack>
          </Box>
        ))}
        {failed && (
          <Typography role="alert" sx={{ fontSize: 12, color: colors.text.extension.negative }}>
            {isFetchNextPageError
              ? "Couldn’t load older history. Please retry."
              : "Couldn’t refresh history. Please retry."}
          </Typography>
        )}
        {(hasNextPage || isFetchNextPageError) && (
          <Button
            size="small"
            disabled={isFetchingNextPage}
            onClick={loadOlder}
            sx={{ alignSelf: "flex-start", textTransform: "none" }}
          >
            {olderHistoryLabel()}
          </Button>
        )}
        {failed && !isFetchNextPageError && (
          <Button size="small" onClick={load}>
            Retry
          </Button>
        )}
      </Stack>
    );
  };

  const disclosure = (
    <Button
      size="small"
      variant="text"
      onClick={toggle}
      disabled={!stepUid}
      aria-expanded={open}
      aria-controls={contentId}
      startIcon={open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      sx={{ textTransform: "none", fontSize: 12 }}
    >
      {open ? "Hide" : "Show"}{inline ? " history" : ""}
    </Button>
  );
  const content = <Collapse id={contentId} in={open} unmountOnExit>{renderHistory()}</Collapse>;
  if (inline) return <Box sx={{mt: 1}}>{disclosure}{content}</Box>;
  return (
    <WidgetCard
      label="Step history"
      icon={<History size={14} color={colors.text.secondary} />}
      action={disclosure}
    >
      {content}
    </WidgetCard>
  );
};
